import { Instagram, Facebook, Mail, MapPin, ArrowRight, ArrowUp } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@/i18n/I18nProvider";
import { openCookiePreferences } from "@/components/festival/CookieBanner";
import { useBranding } from "@/components/festival/BrandingProvider";

// Editable content props — vervang waardes makkelijk via contentbeheer later.
export type FooterContent = {
  email?: string;
  instagram?: string;
  facebook?: string;
  ticketPartner?: string;
  poweredBy?: string | null; // bv. "Delicious Media" — toggle uit door op null te zetten
  organisation?: string | null; // bewust leeg standaard
};

const defaults: Required<FooterContent> = {
  email: "info@openluchtfilmfestival.nl",
  instagram: "https://www.instagram.com/openluchtfilmfestival/",
  facebook: "https://www.facebook.com/openluchtfilmfestivalapeldoorn/",
  ticketPartner: "#",
  poweredBy: null,
  organisation: null,
};

export function Footer(props: FooterContent = {}) {
  const c = { ...defaults, ...props };
  const { t } = useI18n();
  const { logoFullUrl } = useBranding();
  return (
    <footer className="border-t border-border bg-night-deep">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          {/* Logo & tagline */}
          <div className="md:col-span-5">
            <div className="flex items-center">
              <img
                src={logoFullUrl}
                alt="Openlucht Filmfestival"
                width={1706}
                height={398}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto md:h-14"
              />
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-foreground/75">
              {t.footer.tagline}
            </p>
            <p className="mt-6 font-mono-cinema text-[10px] uppercase tracking-widest text-foreground/60">
              {t.footer.edition}
            </p>
          </div>

          {/* Location + related links */}
          <div className="md:col-span-4">
            <h2 className="text-xs uppercase tracking-widest text-foreground/75">{t.footer.location}</h2>
            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-foreground/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>
                De bosweide van Stadspark Berg &amp; Bos
              </span>
            </p>

            <h3 className="mt-8 text-xs uppercase tracking-widest text-foreground/75">Meer informatie</h3>
            <ul className="mt-3 space-y-2">
              <li>
                <Link
                  to="/locatie"
                  className="group inline-flex items-center gap-1.5 text-sm text-foreground/75 transition-colors hover:text-gold"
                >
                  <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform group-hover:translate-x-0.5" />
                  Over het park
                </Link>
              </li>
              <li>
                <Link
                  to="/duurzaamheid"
                  className="group inline-flex items-center gap-1.5 text-sm text-foreground/75 transition-colors hover:text-gold"
                >
                  <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform group-hover:translate-x-0.5" />
                  Duurzaamheid
                </Link>
              </li>
              <li>
                <Link
                  to="/pers"
                  className="group inline-flex items-center gap-1.5 text-sm text-foreground/75 transition-colors hover:text-gold"
                >
                  <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform group-hover:translate-x-0.5" />
                  Pers
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h2 className="text-xs uppercase tracking-widest text-foreground/75">{t.footer.contact}</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground/75">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-gold" />
                <a href={`mailto:${c.email}`} className="transition-colors hover:text-gold">{c.email}</a>
              </li>
            </ul>

            <h3 className="mt-8 text-xs uppercase tracking-widest text-foreground/75">{t.footer.followUs}</h3>
            <ul className="mt-3 flex items-center gap-1">
              <li>
                <a
                  href={c.instagram}
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex rounded-full p-2 text-foreground/70 transition-colors hover:bg-gold/10 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50"
                >
                  <Instagram className="h-6 w-6" />
                </a>
              </li>
              <li>
                <a
                  href={c.facebook}
                  aria-label="Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex rounded-full p-2 text-foreground/70 transition-colors hover:bg-gold/10 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50"
                >
                  <Facebook className="h-6 w-6" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 flex flex-col items-start justify-between gap-5 border-t border-border pt-6 text-xs text-foreground/75 md:flex-row md:items-center">
          <p>
            {t.footer.copyright.replace("{year}", String(new Date().getFullYear()))} ·{" "}
            <span dangerouslySetInnerHTML={{ __html: t.footer.rights }} />
            {c.organisation && <span> · {c.organisation}</span>}
            {c.poweredBy && <span> · Powered by {c.poweredBy}</span>}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacybeleid" className="transition-colors hover:text-gold">{t.footer.privacy}</Link>
            <Link to="/vertrouwen" className="transition-colors hover:text-gold">Vertrouwen</Link>
            <button
              type="button"
              onClick={() => openCookiePreferences()}
              className="transition-colors hover:text-gold"
            >
              Cookie-instellingen wijzigen
            </button>
            <a href={c.ticketPartner} className="transition-colors hover:text-gold">{t.footer.ticketPartner}</a>
            <span className="hidden h-3 w-px bg-border md:inline-block" />
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="group inline-flex items-center gap-1.5 transition-colors hover:text-gold"
            >
              <ArrowUp className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5" />
              {t.common.backToTop}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
